import React, { useState } from "react";

import { MenuItem } from "@mui/material";
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { Grid, Stack } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { useActualInventoryContext } from "Hooks/useActualInventoryContext";
import { ItemException } from "Models/item.exception";
import { Symbol } from "Models/symbol";
import { Placement } from "Util/enums";
import { Select } from "Components/Form";
import Button, { Color } from "Components/Form/Button";
import Checkbox from "Components/Form/Checkbox";
import Chip from "Components/Form/Chip";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import useStyles from "./styles";

const ItemConditions = () => {
  const { t: translate } = useTranslation();

  const { itemJob, setItemJob, locationSymbols, exceptionSymbols } = useActualInventoryContext();

  const classes = useStyles();

  const [locationSymbol, setLocationSymbol] = useState<Symbol>();
  const [exceptionSymbol, setExceptionSymbol] = useState<Symbol>();
  const [atOrigin, setAtOrigin] = useState<boolean>(false);
  const [atDestination, setAtDestination] = useState<boolean>(false);

  const handleAddCondition = () => {
    if (locationSymbol && exceptionSymbol) {
      const itemJobUpdated = { ...(itemJob || {}) };

      const baseException = {
        exceptionSymbol,
        exceptionSymbolId: exceptionSymbol?.id,
        locationSymbol,
        locationSymbolId: locationSymbol?.id,
        itemJobId: itemJobUpdated?.id,
      } as ItemException;

      const exceptionsToAdd: ItemException[] = [];

      if (atOrigin) exceptionsToAdd.push({ ...baseException, isDelivery: false });
      if (atDestination) exceptionsToAdd.push({ ...baseException, isDelivery: true });
      itemJobUpdated.exceptions = [...(itemJobUpdated.exceptions || []), ...exceptionsToAdd];
      setItemJob(itemJobUpdated);
      setLocationSymbol(undefined);
      setExceptionSymbol(undefined);
      setAtOrigin(false);
      setAtDestination(false);
      NotificationToast.success(translate("conditionAddedSuccessfully"), {
        id: "actual.inventory.condition.add",
      });
    }
  };

  const handleRemoveCondition = (itemExceptionRemove: ItemException) => {
    const itemJobUpdated = { ...(itemJob || {}) };

    if (itemJobUpdated?.exceptions && itemJobUpdated?.exceptions?.length > 0) {
      const index = itemJobUpdated?.exceptions.findIndex(
        (itemException) =>
          itemException?.exceptionSymbolId === itemExceptionRemove?.exceptionSymbolId &&
          itemException?.locationSymbolId === itemExceptionRemove?.locationSymbolId &&
          itemException?.itemJobId === itemExceptionRemove?.itemJobId &&
          itemException?.isDelivery === itemExceptionRemove?.isDelivery
      );

      delete itemJobUpdated.exceptions[index];
      itemJobUpdated.exceptions = itemJobUpdated.exceptions.filter(Boolean);
      setItemJob(itemJobUpdated);
    }
  };

  const renderLocationSymbols = () => {
    if (locationSymbols && locationSymbols?.length > 0) {
      return locationSymbols.map((locationSymbol) => {
        return (
          <MenuItem key={locationSymbol.id} value={locationSymbol.id}>
            {locationSymbol.name}
          </MenuItem>
        );
      });
    }

    return null;
  };

  const renderExceptionSymbols = () => {
    if (exceptionSymbols && exceptionSymbols?.length > 0) {
      return exceptionSymbols.map((exceptionSymbol) => {
        return (
          <MenuItem key={exceptionSymbol.id} value={exceptionSymbol.id}>
            {exceptionSymbol.name}
          </MenuItem>
        );
      });
    }

    return null;
  };

  const renderConditions = (isDelivery?: boolean) => {
    const exceptionsFiltered =
      itemJob && itemJob?.exceptions && itemJob?.exceptions?.length > 0
        ? itemJob?.exceptions?.filter((itemException) =>
            isDelivery
              ? itemException?.isDelivery === true
              : itemException?.isDelivery === false || itemException?.isDelivery === undefined
          )
        : [];

    if (exceptionsFiltered && exceptionsFiltered?.length > 0) {
      return exceptionsFiltered?.map((itemException) => {
        if (
          itemException &&
          itemException?.exceptionSymbol &&
          itemException?.exceptionSymbol?.prefix &&
          itemException?.locationSymbol &&
          itemException?.locationSymbol?.prefix
        ) {
          return (
            <Grid item>
              <Chip
                label={`${itemException?.exceptionSymbol?.prefix || ""} ${
                  itemException?.locationSymbol?.prefix || ""
                }`}
                onDelete={() => handleRemoveCondition(itemException)}
                size="small"
              />
            </Grid>
          );
        }

        return <></>;
      });
    }

    return (
      <Grid item>
        <span
          data-cy={`no-conditions-${isDelivery ? "destination" : "origin"}`}
          className={classNames(classes.subTitle, classes.noConditions)}
        >
          {translate("noConditions")}
        </span>
      </Grid>
    );
  };

  return (
    <div>
      <span className={classes.title}>{translate("itemConditions")}</span>
      <Grid container className={classes.gridForm} direction="column" spacing={2}>
        <Grid item>
          <Stack direction="column" spacing={1}>
            <span className={classes.subTitle}>{translate("conditionAt")}</span>
            <Stack direction="row" spacing={2}>
              <Checkbox
                checked={atOrigin}
                onChange={(_, checked) => setAtOrigin(checked)}
                label={translate("origin")}
                labelPlacement={Placement.end}
                data-cy="checkbox-origin"
              />
              <Checkbox
                checked={atDestination}
                onChange={(_, checked) => setAtDestination(checked)}
                label={translate("destination")}
                labelPlacement={Placement.end}
                data-cy="checkbox-destination"
              />
            </Stack>
          </Stack>
        </Grid>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          spacing={2}
          alignItems="flex-end"
        >
          <Grid item xs={12} sm={5}>
            <Select
              label={translate("condition")}
              value={exceptionSymbol?.id}
              placeholder={translate("select")}
              onChange={(event) => {
                const exceptionSymbolFound =
                  exceptionSymbols && exceptionSymbols?.length > 0
                    ? exceptionSymbols?.find(
                        (exceptionSymbol) => exceptionSymbol?.id === Number(event.target.value)
                      )
                    : null;

                if (exceptionSymbolFound) setExceptionSymbol(exceptionSymbolFound);
              }}
              displayEmpty
              data-cy="condition"
            >
              {renderExceptionSymbols()}
            </Select>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Select
              label={translate("exceptionLocationCode")}
              value={locationSymbol?.id}
              placeholder={translate("select")}
              onChange={(event) => {
                const locationSymbolFound =
                  locationSymbols && locationSymbols?.length > 0
                    ? locationSymbols?.find(
                        (locationSymbol) => locationSymbol?.id === Number(event.target.value)
                      )
                    : null;

                if (locationSymbolFound) setLocationSymbol(locationSymbolFound);
              }}
              displayEmpty
              data-cy="exception-location-code"
            >
              {renderLocationSymbols()}
            </Select>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              startIcon={<AddCircleOutlineIcon htmlColor="#FFFFFF" />}
              customColor={Color.SECONDARY}
              data-cy="add-condition-button"
              onClick={handleAddCondition}
              disabled={!locationSymbol || !exceptionSymbol || (!atOrigin && !atDestination)}
            >
              {translate("add")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <div className={classes.separator}>&nbsp;</div>
      <div className={classes.containerConditions}>
        <span className={classes.subTitle}>{translate("conditionsAtOrigin")}</span>
        <Grid
          className={classes.gridConditions}
          data-cy="list-conditions-origin"
          container
          direction="row"
          spacing={1}
        >
          {renderConditions()}
        </Grid>
      </div>
      <div className={classes.containerConditions}>
        <span className={classes.subTitle}>{translate("conditionsAtDestination")}</span>
        <Grid
          className={classes.gridConditions}
          data-cy="list-conditions-destination"
          container
          direction="row"
          spacing={1}
        >
          {renderConditions(true)}
        </Grid>
      </div>
    </div>
  );
};

export default ItemConditions;
